import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/context/AuthProvider";
import { apiRequest } from "src/services/apiClient";
import { ActivationCelebration } from "src/components/ActivationCelebration/ActivationCelebration";
import "./trialEndModal.scss";

const PLAN_LABELS: Record<number, string> = {
    1: "BASIC",
    2: "PLUS",
    3: "PRO",
    4: "ENTERPRISE",
};

const PLAN_PRICES: Record<number, string> = {
    1: "€79",
    2: "€99",
    3: "€149",
    4: "€299",
};

type Step = "main" | "feedback" | "done-accepted" | "done-feedback";

function resolveGreeting(salutation: string | undefined, t: (k: string) => string): string {
    if (salutation === "Herr" || salutation === "H") return t("trial.greetingMale");
    if (salutation === "Frau" || salutation === "F") return t("trial.greetingFemale");
    return t("trial.greeting");
}

export function TrialEndModal() {
    const { t } = useTranslation();
    const { user, updateUser, logout } = useAuth();
    const [step, setStep] = useState<Step>("main");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState("");
    const [feedbackError, setFeedbackError] = useState<string | null>(null);

    const status = user?.praxis?.subscriptionStatus;
    if (status !== "pending_acceptance") return null;

    const doctor = user?.doctor;
    const plan = user?.praxis?.businessPlan ?? 1;
    const planLabel = PLAN_LABELS[plan] ?? "BASIC";
    const planPrice = PLAN_PRICES[plan] ?? "€79";

    const greeting = resolveGreeting(doctor?.salutation, t);
    const fullName = [doctor?.title, doctor?.firstName, doctor?.lastName].filter(Boolean).join(" ");

    const handleAccept = async () => {
        setLoading(true);
        setError(null);
        try {
            await apiRequest("/trial/accept", { method: "POST" });
            updateUser({ praxis: { ...user!.praxis, subscriptionStatus: "active" } });
            setStep("done-accepted");
        } catch {
            setError(t("common.unknownError"));
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        setLoading(true);
        try {
            await apiRequest("/trial/decline", { method: "POST" });
        } catch {
            // Best-effort — log out regardless
        }
        logout();
    };

    const handleFeedbackSubmit = async () => {
        if (!feedback.trim()) {
            setFeedbackError(t("common.fieldRequired", "Please enter your feedback."));
            return;
        }
        setLoading(true);
        setFeedbackError(null);
        try {
            await apiRequest("/bug-report", {
                method: "POST",
                body: JSON.stringify({
                    title: "Trial rejection feedback",
                    description: feedback.trim(),
                    type: "suggestion",
                }),
            });
        } catch {
            // Best-effort — don't block the user if the request fails
        } finally {
            setLoading(false);
            setStep("done-feedback");
        }
    };

    if (step === "done-accepted") {
        return <ActivationCelebration onClose={() => window.location.reload()} />;
    }

    return createPortal(
        <div className="trial-modal-overlay" role="dialog" aria-modal="true">
            <div className="trial-modal">
                <div className="trial-modal__header">
                    <span className="trial-modal__logo">medinvoice®</span>
                </div>

                {/* ── Main step ─────────────────────────────────────────── */}
                {step === "main" && (
                    <div className="trial-modal__body trial-modal__body--left">
                        <p className="trial-modal__greeting">
                            {greeting} {fullName},
                        </p>

                        <p
                            className="trial-modal__paragraph"
                            dangerouslySetInnerHTML={{
                                __html: t("trial.expiredBody", { plan: planLabel }),
                            }}
                        />

                        <p className="trial-modal__paragraph">
                            {t("trial.expiredContinue")}
                        </p>

                        <div className="trial-modal__plan-card">
                            <div className="trial-modal__plan-row">
                                <span className="trial-modal__plan-key">{t("trial.selectedPlan")}</span>
                                <span className={`trial-modal__plan-badge trial-modal__plan-badge--${plan}`}>
                                    {planLabel}
                                </span>
                            </div>
                            <div className="trial-modal__plan-row trial-modal__plan-row--price">
                                <span className="trial-modal__plan-key">{t("trial.price")}</span>
                                <span className="trial-modal__plan-price">
                                    {planPrice}<span className="trial-modal__plan-per"> {t("trial.perMonth")} *</span>
                                </span>
                            </div>
                        </div>

                        <p className="trial-modal__paragraph trial-modal__paragraph--muted">
                            {t("trial.expiredProration")}
                        </p>

                        <p className="trial-modal__paragraph trial-modal__paragraph--muted">
                            {t("trial.expiredFeedbackPrompt")}
                        </p>

                        {error && <p className="trial-modal__error">{error}</p>}

                        <div className="trial-modal__actions">
                            <button
                                className="trial-modal__btn trial-modal__btn--primary"
                                onClick={handleAccept}
                                disabled={loading}
                            >
                                {loading ? "…" : t("trial.completeOrder")}
                            </button>
                            <button
                                className="trial-modal__btn trial-modal__btn--secondary"
                                onClick={() => setStep("feedback")}
                                disabled={loading}
                            >
                                {t("trial.sendFeedback")}
                            </button>
                            <button
                                className="trial-modal__btn trial-modal__btn--reject"
                                onClick={handleReject}
                                disabled={loading}
                            >
                                {t("trial.reject")}
                            </button>
                        </div>

                        <p className="trial-modal__vat-note">{t("trial.vatNote")}</p>
                    </div>
                )}

                {/* ── Feedback step ──────────────────────────────────────── */}
                {step === "feedback" && (
                    <div className="trial-modal__body trial-modal__body--left">
                        <div className="trial-modal__feedback-header">
                            <p className="trial-modal__section-title">{t("trial.feedbackTitle")}</p>
                            <p className="trial-modal__paragraph trial-modal__paragraph--muted">
                                {t("trial.feedbackDescription")}
                            </p>
                        </div>

                        <textarea
                            className="trial-modal__textarea"
                            rows={5}
                            placeholder={t("trial.feedbackPlaceholder")}
                            value={feedback}
                            onChange={(e) => { setFeedback(e.target.value); setFeedbackError(null); }}
                            disabled={loading}
                        />

                        {feedbackError && <p className="trial-modal__error">{feedbackError}</p>}

                        <div className="trial-modal__actions">
                            <button
                                className="trial-modal__btn trial-modal__btn--primary"
                                onClick={handleFeedbackSubmit}
                                disabled={loading || !feedback.trim()}
                            >
                                {loading ? "…" : t("trial.feedbackSubmit")}
                            </button>
                            <button
                                className="trial-modal__btn trial-modal__btn--secondary"
                                onClick={() => { setFeedback(""); setFeedbackError(null); setStep("main"); }}
                                disabled={loading}
                            >
                                {t("trial.feedbackBack")}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Feedback sent step ─────────────────────────────────── */}
                {step === "done-feedback" && (
                    <div className="trial-modal__body trial-modal__body--left">
                        <div className="trial-modal__icon trial-modal__icon--success">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <p className="trial-modal__section-title">{t("trial.feedbackSentTitle")}</p>
                        <p className="trial-modal__paragraph trial-modal__paragraph--muted">
                            {t("trial.feedbackSentBody")}
                        </p>

                        <div className="trial-modal__actions">
                            <button
                                className="trial-modal__btn trial-modal__btn--primary"
                                onClick={handleAccept}
                                disabled={loading}
                            >
                                {loading ? "…" : t("trial.completeOrder")}
                            </button>
                            <button
                                className="trial-modal__btn trial-modal__btn--reject"
                                onClick={handleReject}
                            >
                                {t("trial.logOut")}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Accepted step — rendered outside the modal shell ─── */}
            </div>
        </div>,
        document.body,
    );
}
